/*!
 * Copyright © 2019. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ICollectionDiff } from './collection-diff.interface';

export class MapDiffer<T, U> {
  private _current: Map<U, T>;
  private readonly _identifierFn: (item: T) => U;
  private readonly _equalityFn: (previousItem: T, newItem: T) => boolean;
  private readonly _defaultEqualityFn: (previousItem: T, newItem: T) => boolean = (previousItem: T, newItem: T) =>
    this._identifierFn(previousItem) === this._identifierFn(newItem);

  constructor(identifierFn: (item: T) => U, equalityFn?: (previousItem: T, newItem: T) => boolean) {
    this._current = new Map<U, T>();
    this._identifierFn = identifierFn;
    this._equalityFn = equalityFn || this._defaultEqualityFn;
  }

  public getCurrent(): Map<U, T> {
    return this._current;
  }

  // finds the difference between the collection input and the collection stored in a map as _current
  public diff(collection: T[]): ICollectionDiff<T> {
    const diff: ICollectionDiff<T> = {
      added: [],
      modified: [],
      removed: [],
      unchanged: []
    };

    // newMap will replace the current map stored in _current
    const newMap = new Map<U, T>();
    collection.forEach(item => {
      // add every item to the new map
      newMap.set(this._identifierFn(item), item);

      const currentItem = this._current.get(this._identifierFn(item));
      if (currentItem === undefined) {
        // new item
        diff.added.push(item);
      } else if (this._equalityFn(currentItem, item)) {
        diff.unchanged.push(currentItem);
      } else {
        diff.modified.push(item);
      }
    });

    // find items that were removed
    this._current.forEach((value, key) => {
      if (!newMap.has(key)) {
        diff.removed.push(value);
      }
    });

    // replace the currentMap
    this._current = newMap;

    return diff;
  }
}
